/**
 * Transform a Supabase Storage public URL to use the image render endpoint
 * which serves resized + format-converted images on the fly.
 *
 * When `format` is omitted the transformer negotiates via the browser's
 * `Accept` header (typically returning WebP). Pass an explicit format
 * ("avif" | "webp" | "origin") when driving a <picture> element so each
 * <source> can advertise its exact MIME type.
 *
 * Falls back to the original URL for non-Supabase URLs (local assets, external).
 */
export type ImageFormat = "origin" | "auto" | "avif" | "webp";

export const optimizeImageUrl = (
 url: string | undefined,
 options: { width?: number; quality?: number; format?: ImageFormat } = {},
): string => {
 if (!url) return "";
 // Only transform Supabase Storage public URLs
 if (!url.includes("/storage/v1/object/public/")) return url;

 const { width = 800, quality = 75, format } = options;
 const renderUrl = url.replace(
 "/storage/v1/object/public/",
 "/storage/v1/render/image/public/",
 );
 const sep = renderUrl.includes("?") ? "&" : "?";
 const fmt = format && format !== "auto" ? `&format=${format}` : "";
 return `${renderUrl}${sep}width=${width}&quality=${quality}&resize=contain${fmt}`;
};

/**
 * Build a responsive srcset for Supabase Storage URLs. Returns empty string for
 * non-Supabase URLs (local imports, external), caller should omit the attribute.
 *
 * Pass `format` to build a srcset locked to a single output format, useful
 * for `<source type="image/avif">` / `<source type="image/webp">` variants
 * inside a <picture> tag.
 */
export const buildSrcSet = (
 url: string | undefined,
 widths: number[] = [400, 600, 800, 1200],
 quality = 75,
 format?: ImageFormat,
): string => {
 if (!url || !url.includes("/storage/v1/object/public/")) return "";
 return widths
 .map((w) => `${optimizeImageUrl(url, { width: w, quality, format })} ${w}w`)
 .join(", ");
};

/** True when this URL can be routed through the render endpoint. */
export const isTransformable = (url: string | undefined): boolean =>
 !!url && url.includes("/storage/v1/object/public/");
